import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataList } from "@/components/common/DataList";
import { PageHeader, YesNoBadge } from "@/components/common/Common";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/hooks/useList";
import { api, fmtDateTime, formatError } from "@/lib/api";
import { Field, DeleteButton } from "./CompanyFormDialog";

const RECIPIENTS = { contractor: "Contractor", recruiter: "Recruiter", both: "Contractor + recruiter (CC)" };
const NONE = "__none";

function TemplateDialog({ initial, onClose, onSaved }) {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState({ code: "", name: "", subject: "", body: "", default_recipients: "contractor", checklist_item_code: null, active: true, ...initial });
  const [fields, setFields] = useState([]);
  const [items, setItems] = useState([]);
  const subjectRef = useRef(null);
  const isNew = !initial.id;
  useEffect(() => {
    api.get("/email-templates/merge-fields").then((r) => setFields(r.data));
    api.get("/checklist-items", { params: { page_size: 200 } }).then((r) => setItems(r.data.items));
  }, []);
  const insert = (f) => {
    const tag = `{{${f}}}`;
    if (document.activeElement === subjectRef.current) setForm((s) => ({ ...s, subject: s.subject + tag }));
    else setForm((s) => ({ ...s, body: `${s.body}${tag}` }));
  };
  const save = async () => {
    try {
      if (isNew) await api.post("/email-templates", form); else await api.put(`/email-templates/${initial.id}`, form);
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  const remove = async () => { try { await api.delete(`/email-templates/${initial.id}`); toast.success("Template deleted"); onSaved(); } catch (e) { toast.error(formatError(e)); } };
  const ro = !isAdmin;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New template" : form.name}</DialogTitle><DialogDescription>Merge fields are filled in from the contract when staff send the email. Values are escaped safely.</DialogDescription></DialogHeader>
        <div className="grid lg:grid-cols-[1fr_220px] gap-5">
          <fieldset disabled={ro} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></Field>
              <Field label="Code"><Input value={form.code} disabled={!isNew || ro} onChange={(e) => setForm({ ...form, code: e.target.value })} className="font-mono" data-testid="template-code-input" /></Field>
              <Field label="Default recipients">
                <Select value={form.default_recipients} onValueChange={(v) => setForm({ ...form, default_recipients: v })} disabled={ro}>
                  <SelectTrigger data-testid="template-recipients-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RECIPIENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Tick checklist item when sent">
                <Select value={form.checklist_item_code || NONE} onValueChange={(v) => setForm({ ...form, checklist_item_code: v === NONE ? null : v })} disabled={ro}>
                  <SelectTrigger data-testid="template-checklist-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>None</SelectItem>{items.map((i) => <SelectItem key={i.code} value={i.code}>{i.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Subject"><Input ref={subjectRef} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-subject-input" /></Field>
            <Field label="Body"><RichTextEditor value={form.body} onChange={(v) => setForm((s) => ({ ...s, body: v }))} disabled={ro} testId="template-body-editor" /></Field>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} disabled={ro} data-testid="template-active-switch" />Active</label>
          </fieldset>
          {!ro && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Insert merge field</p>
              <div className="flex flex-wrap lg:flex-col gap-1">
                {fields.map((f) => <button key={f} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(f)} data-testid={`merge-field-${f}`}
                  className="text-left font-mono text-[11px] bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded px-2 py-1">{`{{${f}}}`}</button>)}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {!isNew && isAdmin && <DeleteButton onConfirm={remove} testId="template-delete-button" />}
          <Button variant="outline" onClick={onClose}>{ro ? "Close" : "Cancel"}</Button>
          {!ro && <Button onClick={save} data-testid="template-save-button">Save template</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Templates() {
  const { isAdmin } = useAuth();
  const list = useList("/email-templates", { pageSize: 100 });
  const [editing, setEditing] = useState(null);
  const columns = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "subject", label: "Subject" },
    { key: "default_recipients", label: "Sends to", render: (r) => RECIPIENTS[r.default_recipients] },
    { key: "checklist_item_code", label: "Ticks item", render: (r) => <span className="font-mono text-xs">{r.checklist_item_code || "—"}</span> },
    { key: "active", label: "Active", render: (r) => <YesNoBadge value={r.active} /> },
    { key: "updated_at", label: "Updated", render: (r) => fmtDateTime(r.updated_at) },
  ];
  return (
    <div>
      <PageHeader title="Templates" subtitle="Email templates staff send manually from a contract. Nothing is sent automatically." testId="templates-header"
        actions={isAdmin && <Button onClick={() => setEditing({})} data-testid="new-template-button"><Plus className="h-4 w-4 mr-1" />New template</Button>} />
      <DataList columns={columns} rows={list.items} loading={list.loading} onRowClick={(r) => setEditing(r)} rowTestId="template-row" />
      {editing && <TemplateDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </div>
  );
}
